/**
 * Broadcast attachment validation
 *
 * The backend only accepts image attachments with these extensions:
 * gif, jpeg, jpg, png. It validates the filename extension, not just the
 * MIME type, so the frontend must reject other image/* formats (heic, heif,
 * webp, bmp, svg, etc.) before sending.
 */

export const BROADCAST_ATTACH_ALLOWED_EXTS = ['gif', 'jpeg', 'jpg', 'png'] as const;

export const BROADCAST_ATTACH_MAX_BYTES = 4 * 1024 * 1024; // 4 MB

export const BROADCAST_ATTACH_ACCEPT = 'image/gif,image/jpeg,image/png,.gif,.jpeg,.jpg,.png';

const EXT_REGEX = /\.(gif|jpe?g|png)$/i;

const ALLOWED_MIMES = new Set<string>(['image/gif', 'image/jpeg', 'image/png']);

export function getBroadcastAttachAcceptAttribute(): string {
  return BROADCAST_ATTACH_ACCEPT;
}

export function isBroadcastAttachmentExtensionAllowed(fileName: string): boolean {
  return EXT_REGEX.test(fileName ?? '');
}

export function isBroadcastAttachmentMimeAllowed(mime: string): boolean {
  return ALLOWED_MIMES.has((mime ?? '').toLowerCase());
}

/**
 * Validate a File selected for a broadcast attachment.
 * Returns a user-facing error string, or null when the file is acceptable.
 */
export function validateBroadcastAttachment(file: File): string | null {
  if (!file) return 'No file selected.';

  if (file.size > BROADCAST_ATTACH_MAX_BYTES) {
    const sizeMb = (file.size / 1024 / 1024).toFixed(1);
    return `File too large — max 4 MB (this file is ${sizeMb} MB).`;
  }

  if (!isBroadcastAttachmentExtensionAllowed(file.name)) {
    return 'Attachment filename must use an allowed extension: gif, jpeg, jpg, png.';
  }

  if (file.type && !isBroadcastAttachmentMimeAllowed(file.type)) {
    return 'Attachment filename must use an allowed extension: gif, jpeg, jpg, png.';
  }

  return null;
}

/**
 * Read a File into a base64 string with the "data:<mime>;base64," prefix
 * stripped — the API expects raw base64 only.
 */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string | undefined;
      if (!dataUrl) {
        reject(new Error('Failed to read file.'));
        return;
      }
      const base64 = dataUrl.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}
